import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";
import { Users, UserCheck, Briefcase, Building2, GraduationCap, Sparkles, TrendingUp, ArrowRight } from "lucide-react";
import { PageHeader, KpiCard, SectionCard } from "@/components/Shared";
import {
  workforce,
  skillDistribution,
  departments,
  keyRoles,
  workforceOpportunities,
} from "@/data/extraData";
import { turnaroundRoadmap } from "@/data/extraData";

const tooltipStyle = {
  contentStyle: { backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 },
};

export default function Workforce() {
  const navigate = useNavigate();

  const goToPhase = (phaseNum) => {
    navigate(`/value-creation#phase-${phaseNum}`);
    setTimeout(() => {
      const el = document.getElementById(`phase-${phaseNum}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  return (
    <div data-testid="page-workforce">
      <PageHeader
        eyebrow="Capacity & Workforce"
        title="Operational readiness — the people behind the asset."
        subtitle="Workforce composition, skills, organisational structure and the optimisation levers that come with the team."
      />

      {/* 1. Workforce Overview KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <KpiCard testid="wf-employees" label="Total Employees" value={workforce.totalEmployees.toString()} unit="permanent" />
        <KpiCard testid="wf-contractors" label="Contractors" value={workforce.contractors.toString()} unit="active" accent="amber" />
        <KpiCard testid="wf-consultants" label="Consultants" value={workforce.consultants.toString()} unit="specialist" />
        <KpiCard testid="wf-tenure" label="Avg Tenure" value={workforce.averageTenure} unit="years" accent="green" />
        <KpiCard testid="wf-attrition" label="Attrition" value={workforce.attritionAnnual} unit="annual" />
        <KpiCard testid="wf-union" label="Union Members" value={workforce.unionMembership} unit="of permanent" />
      </div>

      {/* 2. Skill Distribution */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <SectionCard title="Skill Distribution" description="Composition of the permanent workforce">
          <div className="h-72" data-testid="skill-distribution-chart">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={skillDistribution} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={2}>
                  {skillDistribution.map((d) => <Cell key={d.name} fill={d.color} />)}
                </Pie>
                <Tooltip {...tooltipStyle} formatter={(v) => `${v}%`} />
                <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Workforce Insights" description="Operational continuity at a glance" className="xl:col-span-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InsightTile
              icon={UserCheck}
              title="Workforce stability"
              body="4.8% annual attrition vs sector median 9.2%. Average tenure of 7.2 years indicates a deeply embedded operational team."
              accent="green"
            />
            <InsightTile
              icon={GraduationCap}
              title="Skills depth"
              body="28% skilled professionals (engineers, geologists, technicians) — well above SA mining sector norm of 19%."
              accent="amber"
            />
            <InsightTile
              icon={Briefcase}
              title="Operational continuity"
              body="No work stoppage in 36 months. Recognised union with active 3-year wage agreement valid through 2026."
              accent="green"
            />
            <InsightTile
              icon={Users}
              title="Local hiring"
              body="73% of operational workforce drawn from Mopani District — supports BBBEE targets and community licence."
              accent="amber"
            />
          </div>
        </SectionCard>
      </div>

      {/* 3. Organisational Structure */}
      <SectionCard
        title="Organisational Structure"
        description="High-level departments and reporting"
        className="mb-6"
        action={<Building2 size={16} className="text-slate-700" />}
      >
        {/* Top-level GM */}
        <div className="flex flex-col items-center mb-6" data-testid="org-structure">
          <div className="bg-slate-900 text-white rounded-lg px-6 py-3 shadow-md">
            <div className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">Reports to Board</div>
            <div className="font-display text-base font-semibold mt-0.5">General Manager (GM)</div>
          </div>
          {/* Connector */}
          <div className="w-px h-6 bg-slate-300" />
          <div className="w-full max-w-4xl h-px bg-slate-300" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {departments.map((d) => (
            <div
              key={d.name}
              data-testid={`dept-${d.name.split(" ")[0].toLowerCase()}`}
              className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow relative"
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-px h-3 bg-slate-300" />
              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">{d.head}</div>
              <h4 className="font-display text-sm font-semibold text-slate-900 mt-1">{d.name}</h4>
              <div className="text-xs text-slate-500 mt-2">{d.key}</div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-slate-500">Headcount</span>
                <span className="font-display text-base font-bold text-slate-900">{d.headcount}</span>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* 4. Key Technical Roles */}
      <SectionCard
        title="Key Technical Roles"
        description="Critical-path roles · vacancies and succession status"
        className="mb-6"
      >
        <div className="overflow-x-auto" data-testid="key-roles-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-3 pr-4 font-semibold">Role</th>
                <th className="py-3 pr-4 font-semibold w-24">Count</th>
                <th className="py-3 pr-4 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {keyRoles.map((r) => (
                <tr key={r.role} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="py-3 pr-4 font-medium text-slate-900">{r.role}</td>
                  <td className="py-3 pr-4 font-display text-base font-bold text-slate-900">{r.count}</td>
                  <td className="py-3 pr-4 text-slate-700 text-xs">
                    {r.status.includes("vacant") ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold text-[11px]">
                        {r.status}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold text-[11px]">
                        {r.status}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* 5. Workforce Optimisation Opportunities */}
      <SectionCard
        title="Workforce Optimisation Opportunities"
        description="Each opportunity is mapped to a phase of the Value Creation roadmap — click to jump"
        action={<Sparkles size={16} className="text-amber-700" />}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="workforce-opportunities">
          {workforceOpportunities.map((o, i) => {
            const phase = turnaroundRoadmap[o.phase - 1];
            return (
              <button
                key={o.title}
                onClick={() => goToPhase(o.phase)}
                data-testid={`workforce-opp-${i}`}
                className="text-left bg-amber-50/40 border border-amber-100 rounded-lg p-4 hover:shadow-md hover:border-amber-300 transition-all group"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider text-white"
                      style={{ background: phase.color }}
                      data-testid={`workforce-opp-${i}-phase`}
                    >
                      Phase {o.phase}
                    </span>
                    <span className="text-[10px] text-slate-500 font-semibold">
                      {phase.duration}
                    </span>
                  </div>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-700 text-white text-[10px] font-bold uppercase tracking-wider flex-shrink-0">
                    <TrendingUp size={10} /> {o.saving}
                  </span>
                </div>
                <h4 className="font-display text-base font-semibold text-slate-900 mb-2">{o.title}</h4>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{o.impact}</p>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 group-hover:text-amber-700 transition-colors">
                  <span>Trace to roadmap → {phase.title}</span>
                  <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                </div>
              </button>
            );
          })}
        </div>
      </SectionCard>
    </div>
  );
}

function InsightTile({ icon: Icon, title, body, accent }) {
  const cls = accent === "green"
    ? "bg-emerald-50 border-emerald-200 text-emerald-700"
    : "bg-amber-50 border-amber-200 text-amber-700";
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className={`h-8 w-8 rounded-md ${cls.split(" ")[0]} border ${cls.split(" ")[1]} flex items-center justify-center mb-2`}>
        <Icon size={14} className={cls.split(" ")[2]} />
      </div>
      <h4 className="font-display text-sm font-semibold text-slate-900">{title}</h4>
      <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{body}</p>
    </div>
  );
}
